import React from 'react';

import { Clear } from '@mui/icons-material';
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	InputAdornment,
	MenuItem,
	TextField,
	Tooltip,
} from '@mui/material';

import { EnumModel, PrimitiveType, PropertyTypes } from '../../../api/api';
import { getRequiredFieldProps, usePropertySaveValidation } from './usePropertySaveValidation';

export interface Property {
	name: string;
	newName: string;
	description: string;
	type: PrimitiveType;
	defaultValue?: string;
	isMultiple: boolean;
	isRequired: boolean;
	propertyType: PropertyTypes;
	enumId?: string;
	enumContextId?: string;
}

interface EditSchemaPropertyDialogProps {
	open: boolean;
	property: Property;
	onClose: () => void;
	onSave: (updatedProperty: Property) => void;
	enumModels: EnumModel[];
}

export const EditSchemaPropertyDialog: React.FC<EditSchemaPropertyDialogProps> = ({ open, property, onClose, onSave, enumModels }) => {
	const [formData, setFormData] = React.useState<Property>(property);
	const [selectedEnum, setSelectedEnum] = React.useState<EnumModel | null>(() => enumModels.find((e) => e.id === property.enumId!) || null);
	const isNameValid = formData.newName.trim().length > 0;
	const isTypeValid = !!formData.type;
	const canSave = isNameValid && isTypeValid;
	const { saveAttempted, validateForSave, resetValidation } = usePropertySaveValidation(canSave);

	React.useEffect(() => {
		const found = enumModels.find((e) => e.id === property.enumId) || null;
		setSelectedEnum(found);
		setFormData(property);
		resetValidation();
	}, [property, enumModels, resetValidation]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = e.target;
		setFormData((prev) => ({ ...prev, [name]: checked }));
	};

	const handleClearDefaultValue = () => {
		setFormData((prev) => ({ ...prev, defaultValue: '' }));
	};

	const handleEnumTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const enumType = enumModels.find((enumModel) => enumModel.id === e.target.value) ?? null;
		setSelectedEnum(enumType);
		setFormData((prev) => ({
			...prev,
			enumId: enumType?.id,
			enumContextId: enumType?.contextId,
			defaultValue: '',
		}));
	};

	const handleSave = () => {
		if (!validateForSave()) {
			return;
		}

		const nextFormData = { ...formData };
		if (formData.defaultValue) {
			const enumItem = selectedEnum?.items.find((item) => item.name === formData.defaultValue);
			nextFormData.defaultValue = enumItem ? enumItem.id : formData.defaultValue;
		}
		onSave(nextFormData);
	};

	const defaultValueEndAdornment = formData.defaultValue ? (
		<InputAdornment position="end" sx={{ mr: formData.propertyType === PropertyTypes.Enum ? 2 : 0 }}>
			<Tooltip title="Clear default value">
				<IconButton
					size="small"
					edge="end"
					aria-label="Clear default value"
					onMouseDown={(event) => event.preventDefault()}
					onClick={(event) => {
						event.stopPropagation();
						handleClearDefaultValue();
					}}
				>
					<Clear fontSize="small" />
				</IconButton>
			</Tooltip>
		</InputAdornment>
	) : null;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Edit Property</DialogTitle>
			<DialogContent dividers>
				<TextField
					margin="normal"
					fullWidth
					{...getRequiredFieldProps(saveAttempted && !isNameValid)}
					label="Name"
					name="newName"
					value={formData.newName}
					onChange={handleChange}
				/>
				<TextField margin="normal" fullWidth label="Description" name="description" value={formData.description} onChange={handleChange} />

				{formData.propertyType === PropertyTypes.Simple && (
					<TextField
						margin="normal"
						fullWidth
						label="Default Value"
						name="defaultValue"
						value={formData.defaultValue ?? ''}
						onChange={handleChange}
						InputProps={{ endAdornment: defaultValueEndAdornment }}
					/>
				)}

				{formData.propertyType === PropertyTypes.Enum && selectedEnum !== null && (
					<>
						<TextField margin="normal" fullWidth select label="Enum Type" name="enumId" value={formData.enumId ?? ''} onChange={handleEnumTypeChange}>
							{enumModels.map((enumModel) => (
								<MenuItem key={enumModel.id} value={enumModel.id}>
									{enumModel.name}
								</MenuItem>
							))}
						</TextField>

						<TextField
							margin="normal"
							fullWidth
							select
							label="Default Enum Value"
							name="defaultValue"
							value={formData.defaultValue ?? ''}
							onChange={handleChange}
							InputProps={{ endAdornment: defaultValueEndAdornment }}
						>
							<MenuItem value="">
								<em>None</em>
							</MenuItem>
							{selectedEnum.items.map((item) => (
								<MenuItem key={item.id} value={item.name}>
									{item.name}
								</MenuItem>
							))}
						</TextField>
					</>
				)}

				<FormControlLabel control={<Checkbox checked={formData.isMultiple} onChange={handleCheckboxChange} name="isMultiple" />} label="Is Multiple" />
				<FormControlLabel control={<Checkbox checked={formData.isRequired} onChange={handleCheckboxChange} name="isRequired" />} label="Is Required" />
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="inherit">
					Cancel
				</Button>
				<Button onClick={handleSave} variant="contained">
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};
